if (!customElements.get('age-verifier')) {
  customElements.define(
    'age-verifier',
    class AgeVerifier extends ModalElement {
      constructor() {
        super();

        this.ageAccepted = false;
        this.backgroundState = new Map();
        this.backgroundObserver = null;
        this.focusGuardFrame = null;
        this.sequenceActive = false;
        this.sequenceGuardFrame = null;
        this.loadTimer = null;
        this.onAgeAccept = this.onAgeAccept.bind(this);
        this.onAgeDecline = this.onAgeDecline.bind(this);
        this.onAgeSelectionReturn = this.onAgeSelectionReturn.bind(this);
        this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
        this.onWindowBlur = this.onWindowBlur.bind(this);

      }

      connectedCallback() {
        super.connectedCallback();

        this.ageVerifierAbortController?.abort();
        this.ageVerifierAbortController = new AbortController();

        this.setAttribute('aria-modal', 'true');
        this.defaultLabelledBy ||= this.getAttribute('aria-labelledby');

        if (!Shopify.designMode) {
          const closeButton = this.querySelector('.drawer__close');
          if (closeButton) {
            closeButton.disabled = true;
            closeButton.hidden = true;
            closeButton.tabIndex = -1;
            closeButton.setAttribute('aria-hidden', 'true');
          }

          const gestureHeader = this.querySelector('gesture-element[tabindex]');
          if (gestureHeader) gestureHeader.tabIndex = -1;
        }

        if (this.acceptButton) {
          this.acceptButton.dataset.ageAccept = 'true';
          this.acceptButton.addEventListener('click', this.onAgeAccept, {
            capture: true,
            signal: this.ageVerifierAbortController.signal,
          });
        }

        this.declineButton?.addEventListener('click', this.onAgeDecline, {
          signal: this.ageVerifierAbortController.signal,
        });
        this.returnButton?.addEventListener('click', this.onAgeSelectionReturn, {
          signal: this.ageVerifierAbortController.signal,
        });
        document.addEventListener('focusin', this.onDocumentFocusIn, {
          capture: true,
          signal: this.ageVerifierAbortController.signal,
        });
        window.addEventListener('blur', this.onWindowBlur, {
          signal: this.ageVerifierAbortController.signal,
        });

        this.init();
      }

      disconnectedCallback() {
        this.ageVerifierAbortController?.abort();
        this.ageVerifierAbortController = null;
        this.sequenceActive = false;
        clearTimeout(this.loadTimer);
        this.loadTimer = null;
        cancelAnimationFrame(this.focusRecoveryFrame);
        this.stopFocusGuard();
        this.stopSequenceGuard();
        this.restoreBackground();
        super.disconnectedCallback();
      }

      get shouldLock() {
        return true;
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return 1;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      get cookieName() {
        return 'concept:age-verifier';
      }

      get acceptButton() {
        return this.querySelector('.cookie__buttons button[aria-controls="AgeVerifier"]');
      }

      get declineButton() {
        return this.querySelector('[data-age-decline]');
      }

      get selectionPanel() {
        return this.querySelector('[data-age-selection]');
      }

      get declinePanel() {
        return this.querySelector('[data-age-decline-status]');
      }

      get declineHeading() {
        return this.querySelector('[data-age-decline-heading]');
      }

      get returnButton() {
        return this.querySelector('[data-age-selection-return]');
      }

      get declined() {
        return this.dataset.ageState === 'declined';
      }

      get focusElement() {
        if (this.declined) return this.returnButton || this.declineHeading || this;
        return this.acceptButton || this;
      }

      get sectionRoot() {
        return this.closest('.shopify-section') || this;
      }

      init() {
        clearTimeout(this.loadTimer);
        this.loadTimer = null;
        const shouldVerifyAge = this.testMode || !this.getCookie(this.cookieName);
        this.sequenceActive = shouldVerifyAge && !Shopify.designMode;

        if (this.sequenceActive) {
          this.isolatePendingHardTargets(document.body);
          this.observeBackground();
          this.startSequenceGuard();
        }

        if (shouldVerifyAge) {
          this.load(this.delay);
        }
      }

      load(delay) {
        if (Shopify.designMode) return;

        clearTimeout(this.loadTimer);
        this.loadTimer = setTimeout(() => {
          this.loadTimer = null;
          if (this.isConnected) this.show();
        }, delay * 1000);
      }

      onAgeAccept(event) {
        if (Shopify.designMode) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!this.open) {
          this.ageAccepted = false;
          return;
        }

        this.ageAccepted = true;
        Promise.resolve(this.hide()).then(() => {
          setTimeout(() => {
            if (this.open || !this.hidden) {
              this.ageAccepted = false;
              this.removeCookie(this.cookieName);
              this.startFocusGuard();
              this.recoverFocus();
            }
          });
        });
      }

      onAgeDecline(event) {
        event.preventDefault();
        this.ageAccepted = false;
        this.showDeclineState();
      }

      onAgeSelectionReturn(event) {
        event.preventDefault();
        this.showSelectionState({ focus: true });
      }

      get focusTrapActive() {
        return this.open && !this.ageAccepted && !Shopify.designMode;
      }

      recoverFocus() {
        if (!this.focusTrapActive) return;

        cancelAnimationFrame(this.focusRecoveryFrame);
        this.focusRecoveryFrame = requestAnimationFrame(() => {
          if (!this.focusTrapActive || this.contains(document.activeElement)) return;
          this.focusElement?.focus({ preventScroll: true });
        });
      }

      startFocusGuard() {
        this.stopFocusGuard();

        const guard = () => {
          this.focusGuardFrame = null;
          if (!this.focusTrapActive) return;

          if (document.hasFocus() && !this.contains(document.activeElement)) {
            this.focusElement?.focus({ preventScroll: true });
          }

          if (this.focusTrapActive) {
            this.focusGuardFrame = requestAnimationFrame(guard);
          }
        };

        this.focusGuardFrame = requestAnimationFrame(guard);
      }

      stopFocusGuard() {
        cancelAnimationFrame(this.focusGuardFrame);
        this.focusGuardFrame = null;
      }

      onDocumentFocusIn(event) {
        if (!this.focusTrapActive || this.contains(event.target)) return;

        event.stopPropagation();
        this.recoverFocus();
      }

      onWindowBlur() {
        requestAnimationFrame(() => {
          const activeElement = document.activeElement;
          if (activeElement instanceof HTMLIFrameElement && !this.contains(activeElement)) {
            this.recoverFocus();
          }
        });
      }

      showDeclineState() {
        if (!this.selectionPanel || !this.declinePanel) return;

        this.defaultLabelledBy ||= this.getAttribute('aria-labelledby');
        this.selectionPanel.hidden = true;
        this.declinePanel.hidden = false;
        this.dataset.ageState = 'declined';

        if (this.declineHeading?.id) {
          this.setAttribute('aria-labelledby', this.declineHeading.id);
        }

        this.declineHeading?.focus({ preventScroll: true });
      }

      showSelectionState({ focus = false } = {}) {
        if (!this.selectionPanel || !this.declinePanel) return;

        this.selectionPanel.hidden = false;
        this.declinePanel.hidden = true;
        delete this.dataset.ageState;

        if (this.defaultLabelledBy) this.setAttribute('aria-labelledby', this.defaultLabelledBy);
        else this.removeAttribute('aria-labelledby');

        if (focus) this.declineButton?.focus({ preventScroll: true });
      }

      hide() {
        if (this.open && !this.ageAccepted && !Shopify.designMode) {
          this.resetGesture();
          this.focusElement?.focus({ preventScroll: true });
          return Promise.resolve();
        }

        return super.hide();
      }

      afterShow() {
        this.showSelectionState();
        if (!Shopify.designMode) {
          this.sequenceActive = true;
          this.isolateBackground();
          this.startSequenceGuard();
        }

        super.afterShow();
        if (!Shopify.designMode) this.startFocusGuard();
        this.classList.add('show-image');
      }

      afterHide() {
        this.sequenceActive = false;
        this.stopFocusGuard();
        this.stopSequenceGuard();
        super.afterHide();
        cancelAnimationFrame(this.focusRecoveryFrame);
        this.classList.remove('show-image');
        this.restoreBackground();
        this.showSelectionState();

        // Theme-editor interactions must never create a storefront consent
        // state for the merchant's browser session.
        if (Shopify.designMode) {
          this.ageAccepted = false;
          return;
        }

        if (this.testMode) {
          this.removeCookie(this.cookieName);
          this.ageAccepted = false;
          return;
        }

        if (this.ageAccepted) this.setCookie(this.cookieName, this.expiry);
        this.ageAccepted = false;
      }

      isolateBackgroundElement(element) {
        if (!(element instanceof HTMLElement)) return;
        if (this.contains(element) || element === this.sectionRoot || element.contains(this)) return;
        if (['SCRIPT', 'STYLE', 'LINK', 'META'].includes(element.tagName)) return;

        const hardIsolate = this.isHardIsolationTarget(element);
        const existingState = this.backgroundState.get(element);
        if (existingState) {
          if (existingState.hardIsolate) this.enforceHardIsolation(element);
          return;
        }

        const isolatedStyles = hardIsolate
          ? Object.fromEntries(
            ['display', 'visibility', 'opacity', 'pointer-events'].map((property) => [
              property,
              {
                value: element.style.getPropertyValue(property),
                priority: element.style.getPropertyPriority(property),
              },
            ]),
          )
          : null;

        this.backgroundState.set(element, {
          hardIsolate,
          inert: element.inert,
          ariaHidden: element.getAttribute('aria-hidden'),
          tabIndex: element.getAttribute('tabindex'),
          hiddenAttribute: element.getAttribute('hidden'),
          styleAttributePresent: element.hasAttribute('style'),
          isolatedStyles,
        });

        if (hardIsolate) this.enforceHardIsolation(element);
        else {
          element.inert = true;
          element.setAttribute('aria-hidden', 'true');
        }
      }

      isHardIsolationTarget(element) {
        return element instanceof HTMLIFrameElement
          || element.localName === 'shopify-account'
          || element.localName === 'pandectes-cmp';
      }

      enforceHardIsolation(element) {
        // Pandectes writes display:block!important after insertion. Reassert
        // every visual/input property while the age sequence owns the page.
        element.blur();
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
        element.tabIndex = -1;
        element.hidden = true;
        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('visibility', 'hidden', 'important');
        element.style.setProperty('opacity', '0', 'important');
        element.style.setProperty('pointer-events', 'none', 'important');
        this.recoverFocus();
      }

      hardIsolationApplied(element) {
        return element.inert
          && element.getAttribute('aria-hidden') === 'true'
          && element.getAttribute('tabindex') === '-1'
          && element.hasAttribute('hidden')
          && element.style.getPropertyValue('display') === 'none'
          && element.style.getPropertyPriority('display') === 'important'
          && element.style.getPropertyValue('visibility') === 'hidden'
          && element.style.getPropertyPriority('visibility') === 'important'
          && element.style.getPropertyValue('opacity') === '0'
          && element.style.getPropertyPriority('opacity') === 'important'
          && element.style.getPropertyValue('pointer-events') === 'none'
          && element.style.getPropertyPriority('pointer-events') === 'important';
      }

      captureHardTargetChanges(element, state) {
        const expectedStyles = {
          display: 'none',
          visibility: 'hidden',
          opacity: '0',
          'pointer-events': 'none',
        };
        Object.entries(expectedStyles).forEach(([property, expectedValue]) => {
          const value = element.style.getPropertyValue(property);
          const priority = element.style.getPropertyPriority(property);
          if (value !== expectedValue || priority !== 'important') {
            state.isolatedStyles[property] = { value, priority };
          }
        });
        if (!element.inert) state.inert = false;
        if (element.getAttribute('aria-hidden') !== 'true') state.ariaHidden = element.getAttribute('aria-hidden');
        if (element.getAttribute('tabindex') !== '-1') state.tabIndex = element.getAttribute('tabindex');
        if (!element.hasAttribute('hidden')) state.hiddenAttribute = null;
      }

      restoreIsolatedStyles(element, state) {
        if (!state.isolatedStyles) return;
        Object.entries(state.isolatedStyles).forEach(([property, propertyState]) => {
          if (propertyState.value) element.style.setProperty(property, propertyState.value, propertyState.priority);
          else element.style.removeProperty(property);
        });
        if (!state.styleAttributePresent && element.getAttribute('style') === '') element.removeAttribute('style');
      }

      isolateBackgroundTree(element) {
        if (!(element instanceof HTMLElement) || this.contains(element)) return;

        if (
          element.parentElement === document.body ||
          element instanceof HTMLIFrameElement ||
          element.localName === 'shopify-account' ||
          element.localName === 'pandectes-cmp'
        ) {
          this.isolateBackgroundElement(element);
        }
        element
          .querySelectorAll('iframe, shopify-account, pandectes-cmp')
          .forEach((candidate) => this.isolateBackgroundElement(candidate));
      }

      isolatePendingHardTargets(root) {
        if (!(root instanceof HTMLElement)) return;
        if (this.isHardIsolationTarget(root)) this.isolateBackgroundElement(root);
        root
          .querySelectorAll('iframe, shopify-account, pandectes-cmp')
          .forEach((candidate) => this.isolateBackgroundElement(candidate));
      }

      isolateBackground() {
        [...document.body.children].forEach((element) => this.isolateBackgroundTree(element));

        this.observeBackground();
      }

      observeBackground() {
        if (this.backgroundObserver) return;
        this.backgroundObserver = new MutationObserver((mutations) => {
          if (!this.sequenceActive || Shopify.designMode) return;
          for (const mutation of mutations) {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (this.focusTrapActive) this.isolateBackgroundTree(node);
                else this.isolatePendingHardTargets(node);
              });
              continue;
            }

            const element = mutation.target;
            const state = this.backgroundState.get(element);
            if (state?.hardIsolate && !this.hardIsolationApplied(element)) {
              this.captureHardTargetChanges(element, state);
              this.enforceHardIsolation(element);
            }
          }
        });
        this.backgroundObserver.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'hidden', 'inert', 'aria-hidden', 'tabindex'],
        });
      }

      startSequenceGuard() {
        this.stopSequenceGuard();

        const guard = () => {
          this.sequenceGuardFrame = null;
          if (!this.sequenceActive || Shopify.designMode) return;

          this.backgroundState.forEach((state, element) => {
            if (state.hardIsolate && element.isConnected && !this.hardIsolationApplied(element)) {
              this.captureHardTargetChanges(element, state);
              this.enforceHardIsolation(element);
            }
          });

          if (this.sequenceActive) this.sequenceGuardFrame = requestAnimationFrame(guard);
        };

        this.sequenceGuardFrame = requestAnimationFrame(guard);
      }

      stopSequenceGuard() {
        cancelAnimationFrame(this.sequenceGuardFrame);
        this.sequenceGuardFrame = null;
      }

      restoreBackground() {
        this.backgroundObserver?.disconnect();
        this.backgroundObserver = null;

        this.backgroundState.forEach((state, element) => {
          element.inert = state.inert;
          if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
          else element.setAttribute('aria-hidden', state.ariaHidden);
          if (state.tabIndex === null) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', state.tabIndex);
          if (state.hardIsolate) this.restoreIsolatedStyles(element, state);
          if (state.hiddenAttribute === null) element.removeAttribute('hidden');
          else element.setAttribute('hidden', state.hiddenAttribute);
        });
        this.backgroundState.clear();
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }

      removeCookie(name) {
        document.cookie = `${name}=; max-age=0; path=/`;
      }
    }
  );
}
